<!-- Copyright (c) 2023 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="role-management-main">
    <div>
      <Menus :menu="menus" @menuClicked="menuClicked" />
    </div>

    <div class="table-box-container">
      <TableBox
        title="Role"
        :tableData="roleManagementList"
        :selectedRow.sync="roleMangementListTableBoxSelectedRow"
        :dataType="columnTypeMain"
      />
    </div>

    <PopupEntry
      class="popup-entry-component"
      :show.sync="showPopupEntry"
      v-if="showPopupEntry"
      :title="popupEntryTitle"
      :updatedBy="updatedBy"
      :createdBy="createdBy"
      @save="popupEntrySaveClicked"
      :requiredError="popupEntryRequiredError"
      :successMessages="popupEntrySuccessMessages"
      :messages="popupEntryMessages"
      :errorMessages="popupEntryErrorMessages"
      @requiredClicked="popupEntryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div class="general-inputs">
        <div class="roleID-and-roleName">
          <Input
            placeholder="Role ID"
            :isRequired="true"
            :inputValue.sync="roleIDInputValue"
            :isDisabled="viewModePK"
            :errorMessage.sync="roleIdError"
            ref="roleid"
          />
          <Input
            placeholder="Role Name"
            :inputValue.sync="roleNameInputValue"
            :isDisabled="viewMode"
            :errorMessage.sync="roleNameError"
            ref="rolename"
          />
        </div>

        <Input
          placeholder="Description (Max 200 chars)"
          :inputValue.sync="description"
          :isDisabled="viewMode"
          :errorMessage.sync="descriptionError"
          ref="description"
        />

        <div class="company-and-parentRole">
          <InputQuery
            placeholder="Parent Role"
            :items="parentRoleListMaster"
            :selectedObject.sync="selectedParentRole"
            itemKey="RoleID"
            descKey="Role Name"
            :isDisabled="viewMode"
            :allowFreeText="false"
            ref="parentrole"
          />
        </div>

        <div class="jobType-and-admin">
          <!-- Hidden for now -->
          <div class="jobType-radio-button" v-if="false">
            <RadioButtonSet
              class="radio-button-set-component"
              title="Job Type"
              :buttons="jobTypeRadioButtons"
              labelKey="label"
              :isDisabled="viewMode"
              :isHorizontal="true"
            />
          </div>

          <BoxContainer title="Admin Type">
            <div class="adminType-checkBox">
              <CheckBoxV2
                :isDisabled="viewMode"
                style="margin: 0px 0px 0px 15px"
                title="Super User (Trading)"
                :isChecked.sync="adminTypeSU"
              />
              <CheckBoxV2
                :isDisabled="viewMode"
                style="margin: 0px 0px 0px 15px"
                title="Accounts Admin"
                :isChecked.sync="adminTypeAA"
              />
              <CheckBoxV2
                :isDisabled="viewMode"
                style="margin: 0px 0px 0px 15px"
                title="Job Admin"
                :isChecked.sync="adminTypeJA"
              />
            </div>
          </BoxContainer>
        </div>
      </div>

      <Tab
        class="tab-component"
        :tabs="tabsItems"
        :tabIndex.sync="currentTab"
        @tabClicked="tabClicked"
        :preventTabSwitch="true"
      >
        <!-- System Access -->
        <div class="system-access" slot="System Access">
          <div class="table-box-container">
            <TableBox
              title="Access List"
              :tableData="accessListTableData"
              :selectedRow.sync="accessListTableBoxSelectedRow"
              :dataType="columnTypeMain"
              :maxSelection="100"
            />
          </div>

          <div v-if="!viewMode" class="call-to-action-buttons">
            <button @click="allowSystemAccessButtonClicked">Allow</button>
            <button @click="denySystemAccessButtonClicked">Deny</button>
          </div>
        </div>

        <!-- Function Access -->
        <div slot="Function Access">
          <div>
            <InputQuery
              placeholder="System"
              :items="systemList"
              itemKey="System"
              descKey="Application Name"
              :selectedObject.sync="selectedSystem"
              @selected="getSystemRoleDetails"
              :allowFreeText="false"
              ref="system"
            />
          </div>
          <div
            v-if="roleDetails && roleDetails.SystemAccess && roleDetails.SystemAccess.ModuleAccess && selectedSystem"
          >
            <div
              v-for="(moduleAccess, moduleIndex) in roleDetails.SystemAccess.ModuleAccess"
              :key="'module_' + moduleIndex"
            >
              <BoxContainer
                :title="moduleAccess.AccessDescription"
                :checkBoxIsDisabled="viewMode"
                :isCheckBox="true"
                style="margin-bottom: 10px; margin-top: 20px"
                @checkBoxClicked="moduleAccessCheckboxClicked(moduleAccess)"
                :isChecked.sync="moduleAccess.isChecked"
              >
                <div
                  v-if="moduleAccess.FunctionAccess && moduleAccess.FunctionAccess.length > 0"
                  class="function-access-container"
                >
                  <div
                    v-for="(functionAccess, functionIndex) in moduleAccess.FunctionAccess"
                    :key="'function_' + functionIndex"
                    class="checkbox-container"
                  >
                    <CheckBoxV2
                      :isDisabled="functionAccess.isDisabled || functionAccess.InternalSetting === '1' || viewMode"
                      style="margin: 8px 5px 8px 0"
                      :title="functionAccess.AccessDescription"
                      :isChecked.sync="functionAccess.isChecked"
                    />
                  </div>
                </div>
              </BoxContainer>
            </div>
          </div>
          <div v-else>
            <p v-if="!selectedSystem">Please select a system.</p>
            <p v-else>No data found for the provided System.</p>
          </div>
        </div>

        <!-- Users Bounded by Current Role -->
        <div slot="Users Bounded by Current Role" class="user-Bounded">
          <div class="user-Bounded-List">
            <div v-if="!viewMode" class="call-to-action-buttons">
              <button v-if="userAction != 'View'" @click="addUserButtonClicked">Add User</button>
            </div>

            <div class="table-box-container">
              <TableBox
                title="User Bounded"
                :tableData="usersBoundedTableData"
                :selectedRow.sync="usersBoundedTableBoxSelectedRow"
                :dataType="columnTypeMain"
                :maxSelection="100"
                :hideColumns="usersBoundedTableBoxHideColumns"
              />
            </div>

            <div v-if="userListPopupEntryShow" class="user-List-Popup">
              <div class="user-List">
                <div class="table-box-container">
                  <TableBox
                    title="User ID"
                    :tableData="userListMaster"
                    :selectedRow.sync="userListMasterSelectedRow"
                    :maxSelection="-1"
                  />
                </div>
                <div class="call-to-action-buttons">
                  <button v-if="userAction != 'View'" @click="userPopupAffirmativeClicked">{{ userAction }}</button>
                  <button @click="cancelUserButtonClicked">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Status Code -->
        <div slot="Status Code" class="status-Code">
          <div class="status-Code-List">
            <div v-if="!viewMode" class="call-to-action-buttons">
              <button @click="addStatusCodeButtonClicked" :disabled="showStatusCodePopup">Add Status</button>
              <button @click="deleteStatusCodeButtonClicked" :disabled="showStatusCodePopup">Delete Status</button>
            </div>
            <div class="table-box-container">
              <TableBox
                v-if="showStatusCodeTableBox"
                title="Status Code List"
                :tableData="statusCodeListWithoutDeleted"
                :selectedRow.sync="statusCodeSelectedRow"
                :hideColumns="statusCodeListTableBoxHideColumns"
                :additionalButtons="statusCodeListAdditionalButtons"
                :maxSelection="-1"
                @buttonClicked="editStatusCodeButtonClicked"
              />
            </div>
          </div>

          <div v-if="showStatusCodePopup" class="select-Status-Code-Popup">
            <BoxContainer title="Select Status Code">
              <div>
                <CheckBoxSet
                  title="Status Type"
                  style="display: flex; margin: 5px 0px 5px 8px"
                  :checkBoxes.sync="statusTypeCheckBox"
                  titleKey="checkBoxTitle"
                  :isRequired="true"
                  :isHorizontal="true"
                  :errorMessage.sync="statusTypeError"
                />
              </div>
              <Filters
                v-if="statusAction == 'Add'"
                class="status-filter"
                title="Status Code"
                :tableData="filteredStatusList"
                itemKey="Status"
                itemDesc="Status Desc"
                :selectedObject.sync="selectedStatusCode"
                :isRequired="true"
                noneSelectedText=""
                noneSelectedConfirmButtonText="Select None"
                :errorMessage.sync="statusCodeError"
              />

              <InputQuery
                v-if="statusAction == 'Edit'"
                placeholder="Status Code"
                :items="filteredStatusCodeListMaster"
                itemKey="Status"
                descKey="Status Desc"
                :selectedObject.sync="selectedStatusCodeInput"
                :isRequired="true"
                :errorMessage.sync="statusCodeInputError"
              />
            </BoxContainer>
            <br />
            <div class="call-to-action-buttons">
              <button v-if="statusBeingEdited === null" @click="addStatusAffirmativeClicked">Add</button>
              <button v-else @click="editStatusAffirmativeClicked">Edit</button>
              <button @click="cancelStatusAffirmativeClicked">Cancel</button>
            </div>
          </div>
        </div>
      </Tab>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    systemList: function () {
      return this.accessListTableData.filter((system) => {
        return system.PropertiesValue === 'TRUE';
      });
    },
    statusCodeListAdditionalButtons: function () {
      // hide additional buttons when the PopupEntry is in view mode, need to hide and re-show the TableBox for it to take effect
      return !this.viewMode ? [{ text: 'Edit', index: 0, width: 75 }] : [];
    },
    statusCodeListWithoutDeleted: function () {
      return this.statusCodeListTableData.filter(
        (statusCodeData) =>
          statusCodeData['New'] == '1' || statusCodeData['Edit'] == '1' || statusCodeData['Delete'] == '1',
      );
    },
    statusTypeValues: function () {
      return this.statusTypeCheckBox.reduce((acc, checkBox) => {
        if (checkBox.checkBoxTitle === 'Add new') {
          acc.New = checkBox.isChecked ? '1' : '0';
        } else if (checkBox.checkBoxTitle === 'Edit / Update') {
          acc.Edit = checkBox.isChecked ? '1' : '0';
        } else if (checkBox.checkBoxTitle === 'Delete') {
          acc.Delete = checkBox.isChecked ? '1' : '0';
        }
        return acc;
      }, {});
    },
  },
  data: function () {
    return {
      menus: [
        {
          title: 'New Role',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'cmdAdd',
          enabled: true,
        },
        {
          title: 'Edit Role',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'cmdEdit',
          enabled: true,
        },
        {
          title: 'Delete Role',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'cmdRemove',
          enabled: true,
        },
        { title: 'View Role', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png' },
      ],

      tabsItems: [
        { title: 'System Access' },
        { title: 'Function Access' },
        { title: 'Users Bounded by Current Role' },
        { title: 'Status Code' },
      ],
      currentTab: 0,

      // Not in tab -1, System access tab 0, Function access tab 1, Users Bounded by Current Role tab 2, Status Code tab 3
      errorMapping: [
        { key: 'roleid', tab: -1 },
        { key: 'rolename', tab: -1 },
        { key: 'description', tab: -1 },
        { key: 'company', tab: -1 },
        { key: 'parentrole', tab: -1 },
        { key: 'jobTypeError', tab: -1 },
        { key: 'adminType', tab: -1 },
        { key: 'system', tab: 1 },
        { key: 'userid', tab: 2 },
        { key: 'statuscode', tab: 3 },
      ],

      roleManagementList: [],
      parentRoleListMaster: [],
      userListMaster: [],
      statusCodeListMaster: [],
      userAndStatusRoleDetails: [],
      roleMangementListTableBoxSelectedRow: [],

      // Popup entry state
      showPopupEntry: false,
      updatedBy: '',
      createdBy: '',
      saveAction: '',
      viewMode: false,
      viewModePK: false,
      popupEntryErrorMessages: [],
      popupEntrySuccessMessages: [],
      popupEntryRequiredError: [],
      popupEntryMessages: [],

      // Popup entry common fields
      roleIDInputValue: '',
      roleIdError: '',
      roleNameInputValue: '',
      roleNameError: '',
      description: '',
      descriptionError: '',

      selectedParentRole: null,

      jobTypeRadioButtons: [
        { label: 'Import ', keyValue: 'IM', checked: false },
        { label: 'Export ', keyValue: 'EX', checked: false },
        { label: 'Both ', keyValue: 'BO', checked: true },
      ],

      adminTypeSU: false,
      adminTypeAA: false,
      adminTypeJA: false,

      // System Access
      accessListTableData: [],
      accessListTableBoxSelectedRow: [],

      // Function Access
      selectedSystem: null,
      roleDetails: null,
      roleDetailsCache: {},

      // Users Bounded by Current Role
      usersBoundedTableData: [],
      usersBoundedTableBoxSelectedRow: [],
      userListMasterSelectedRow: [],
      userAction: '',
      userListPopupEntryShow: false,
      usersBoundedTableBoxHideColumns: ['usersBounded_action'],

      // Status Code
      showStatusCodeTableBox: true,
      statusTypeCheckBox: [
        { checkBoxTitle: 'Add new', keyValue: '0', isChecked: false },
        { checkBoxTitle: 'Edit / Update', keyValue: '1', isChecked: false },
        { checkBoxTitle: 'Delete', keyValue: '2', isChecked: false },
      ],
      statusTypeError: '',
      statusBeingEdited: null,
      statusAction: '',
      statusCodeError: '',
      statusCodeInputError: '',
      statusCodeSelectedRow: [],
      statusCodeListTableData: [],
      selectedStatusCode: [],
      selectedStatusCodeInput: null,
      statusCodeListTableBoxHideColumns: ['Role ID', 'Seq', 'OriginalNew', 'OriginalEdit', 'OriginalDelete', '_action'],
      filteredStatusCodeListMaster: [],
      showStatusCodePopup: false,

      // Dropdown
      columnTypeMain: {
        'Date Update': 'date',
      },
    };
  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearPopupEntryMessages();
      this.resetPopupEntryFields();

      switch (e.title) {
        case 'New Role':
          this.popupEntryTitle = 'New Role';
          await this.getParentRoleListMaster();
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;
          this.saveAction = 'N';
          this.accessListTableData = this.accessListTableData.map((system) => {
            system.PropertiesValue = 'FALSE';
            return system;
          });
          break;

        case 'Edit Role':
          if (this.roleMangementListTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Role is selected', `Please select a Role to edit`);
          } else {
            this.$fullscreenLoading('Getting Role Details');
            await this.populateRoleDetails();
            await this.getParentRoleListMaster();
            this.viewMode = false;
            this.viewModePK = true;
            this.showPopupEntry = true;
            this.popupEntryTitle = 'Edit Role';
            this.saveAction = 'E';
          }
          break;

        case 'Delete Role':
          if (this.roleMangementListTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Role Selected', 'Please select a Role to delete');
          } else {
            const isDelete = await this.$showAlert(
              'warning',
              'Delete Role',
              `Are you sure want to Delete Role <b>${this.roleMangementListTableBoxSelectedRow[0]['Role ID']}</b>?`,
              [
                { text: 'Delete', color: 'red', textColor: 'white' },
                { text: 'Cancel', color: 'gray', textColor: 'white' },
              ],
            );

            if (isDelete == 'Delete') {
              const result = await this.$apiCallV2('/RoleManagement/SaveRoleManagement', {
                action: 'D',
                roleid: this.roleMangementListTableBoxSelectedRow[0]['Role ID'],
              });
              if (result[0]['Results'] == 0) {
                // Delete successful
                this.$showAlert(
                  'success',
                  'Role Deleted',
                  `Successfully deleted <b>${this.roleMangementListTableBoxSelectedRow[0]['Role ID']}</b>!`,
                );
              } else {
                // Delete error
                this.$showAlert('error', 'Role NOT Deleted', result[0]['ErrMsg']);
              }
              this.roleMangementListTableBoxSelectedRow = [];
              await this.getRoleManagementList();
            }
          }
          break;

        case 'View Role':
          if (this.roleMangementListTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Role Selected', 'Please select a Role to view');
          } else {
            this.$fullscreenLoading('Getting Role Details');
            await this.populateRoleDetails();
            this.$fullscreenLoading();

            this.popupEntryTitle = 'View Role';
            this.viewMode = true;
            this.viewModePK = true;
            this.showPopupEntry = true;
          }
          break;
      }
    },

    allowSystemAccessButtonClicked: function () {
      if (this.accessListTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No System Selected', 'Please select a system to allow access');
      } else {
        this.accessListTableBoxSelectedRow.forEach((row) => {
          const allowPropValueFound = this.accessListTableData.find((item) => item.System === row.System);
          allowPropValueFound.PropertiesValue = 'TRUE';
        });

        const temp = this.accessListTableData;
        this.accessListTableData = [];
        this.$nextTick(() => {
          this.accessListTableData = temp;
        });
      }
      this.accessListTableBoxSelectedRow = [];
    },

    denySystemAccessButtonClicked: function () {
      if (this.accessListTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No System Selected', 'Please select a system to deny access');
      } else {
        this.accessListTableBoxSelectedRow.forEach((row) => {
          const denyPropValueFound = this.accessListTableData.find((item) => item.System === row.System);
          denyPropValueFound.PropertiesValue = 'FALSE';
        });

        const temp = this.accessListTableData;
        this.accessListTableData = [];
        this.$nextTick(() => {
          this.accessListTableData = temp;
        });
      }
      this.accessListTableBoxSelectedRow = [];
    },

    addUserButtonClicked: function () {
      // Filter userListMaster to exclude users present in usersBoundedTableData
      const existingUser = this.usersBoundedTableData.map((user) => user['User ID']);
      this.userListMaster = this.userListMaster.filter((user) => {
        return !existingUser.includes(user.UserID);
      });
      this.userAction = 'Add';
      this.userListPopupEntryShow = true;
      this.userListMasterSelectedRow = [];
    },

    cancelUserButtonClicked: function () {
      this.userListPopupEntryShow = false;
    },

    userPopupAffirmativeClicked: function () {
      if (this.userListMasterSelectedRow.length == 0) {
        this.$showAlert('error', 'No User Selected', 'Please select a user to add');
        return;
      }

      const modifiedUsersBoundedTableData = [
        ...this.usersBoundedTableData,
        ...this.userListMasterSelectedRow.map((selectedUsersBounded) => ({
          'User ID': selectedUsersBounded['UserID'] ?? '',
          'Full Name': selectedUsersBounded['User Name'] ?? '',
          Department: selectedUsersBounded['Department'] ?? '',
          'Job Title': selectedUsersBounded['Position'] ?? '',
          'Staff Code': selectedUsersBounded['Employee Code'] ?? '',

          usersBounded_action: 'N',
        })),
      ];

      this.usersBoundedTableData = [];
      this.userListPopupEntryShow = false;
      this.userListMasterSelectedRow = [];
      this.$nextTick(() => {
        this.usersBoundedTableData = modifiedUsersBoundedTableData;
        this.usersBoundedTableBoxSelectedRow = [];
      });
    },

    addStatusCodeButtonClicked: function () {
      this.clearStatusPopupFields();

      // Get the list of statuses without deleted ones
      const statusCodeListWithoutDeleted = this.statusCodeListWithoutDeleted;

      // Map statuses from statusCodeListWithoutDeleted
      const statusesWithoutDeleted = statusCodeListWithoutDeleted.map((statusCodeData) => statusCodeData['Status']);

      // Filter statusCodeListMaster to exclude statuses present in statusCodeListTableData
      const filterStatusCodeListMaster = this.statusCodeListMaster.filter((statusCodeData) => {
        return !statusesWithoutDeleted.includes(statusCodeData['Status']);
      });

      this.filteredStatusList = filterStatusCodeListMaster;
      this.statusAction = 'Add';
      this.showStatusCodePopup = true;
      this.statusBeingEdited = null;
    },

    editStatusCodeButtonClicked: function (_buttonText, rowClicked) {
      this.statusBeingEdited = {
        ...rowClicked,
      };
      this.clearStatusPopupFields();
      this.statusAction = 'Edit';

      // Check if selectedStatusCodeInput is already set
      // Filter statusCodeListMaster to keep selectedStatusCodeInput and statuses not in statusCodeListTableData
      if (!this.selectedStatusCodeInput || this.selectedStatusCodeInput.Status !== rowClicked['Status']) {
        this.filteredStatusCodeListMaster = this.statusCodeListMaster.filter((status) => {
          if (status.Status === rowClicked['Status']) {
            return true;
          }
          for (let i = 0; i < this.statusCodeListTableData.length; i++) {
            if (this.statusCodeListTableData[i].Status === status.Status) {
              return false;
            }
          }
          return true;
        });
      }
      // Get the selected status
      this.selectedStatusCodeInput = this.statusCodeListMaster.find((status) => status.Status === rowClicked['Status']);

      this.statusTypeCheckBox = this.updateStatusTypeCheckBox(
        rowClicked['New'] === '1',
        rowClicked['Edit'] === '1',
        rowClicked['Delete'] === '1',
      );

      this.showStatusCodePopup = true;
    },

    deleteStatusCodeButtonClicked: async function () {
      if (this.statusCodeSelectedRow.length === 0) {
        this.$showAlert('error', 'No Status Selected', 'Please select a Status to delete', [{ text: 'OK' }]);
        return;
      }

      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Status',
        'Are you sure you wish to delete the selected status?',
        [{ text: 'Delete', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const statusesToDelete = this.statusCodeSelectedRow;
      let modifiedStatusCodeListTableData = [...this.statusCodeListTableData];

      statusesToDelete.forEach((statusToDelete) => {
        let matchedIndex;

        if (statusToDelete.Seq !== undefined && statusToDelete.Seq !== null) {
          const foundIndex = modifiedStatusCodeListTableData.findIndex(
            (statusCodeData) => statusCodeData.Seq === statusToDelete.Seq,
          );
          if (foundIndex !== -1) {
            matchedIndex = foundIndex;
          }
        }

        if (matchedIndex === undefined && statusToDelete._rowid) {
          const foundIndex = modifiedStatusCodeListTableData.findIndex(
            (statusCodeData) => statusCodeData._rowid === statusToDelete._rowid,
          );
          if (foundIndex !== -1) {
            matchedIndex = foundIndex;
          }
        }

        if (matchedIndex !== undefined) {
          // Update the selected status properties to "0"
          modifiedStatusCodeListTableData[matchedIndex].New = '0';
          modifiedStatusCodeListTableData[matchedIndex].Edit = '0';
          modifiedStatusCodeListTableData[matchedIndex].Delete = '0';
        } else {
          throw new Error('StatusCode - Could not find the row being edited');
        }
      });

      modifiedStatusCodeListTableData = modifiedStatusCodeListTableData.map((statusCodeData) => {
        delete statusCodeData._rowid;
        return statusCodeData;
      });

      this.showStatusCodeTableBox = false;
      this.statusCodeListTableData = [];
      this.statusCodeSelectedRow = [];
      this.$nextTick(() => {
        this.statusCodeListTableData = modifiedStatusCodeListTableData;
        this.showStatusCodeTableBox = true;
      });
    },

    addStatusAffirmativeClicked: function () {
      const isInvalid = this.checkAddStatusPopupFields();
      if (isInvalid) {
        return;
      }

      const modifiedStatusCodeListTableData = [
        ...this.statusCodeListTableData,
        ...this.selectedStatusCode.map((statusCodeData) => ({
          Status: statusCodeData['Status'],
          'Status Description': statusCodeData['Status Desc'],
          ...this.statusTypeValues,
          OriginalNew: '0',
          OriginalEdit: '0',
          OriginalDelete: '0',

          _action: 'N',
        })),
      ];

      this.showStatusCodeTableBox = false; // hide TableBox and re-render on nextTick
      this.statusCodeListTableData = [];
      this.statusCodeSelectedRow = [];
      this.showStatusCodePopup = false;
      this.$nextTick(() => {
        this.statusCodeListTableData = modifiedStatusCodeListTableData;
        this.showStatusCodeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },

    editStatusAffirmativeClicked: function () {
      const isInvalid = this.checkEditStatusPopupFields();
      if (isInvalid) {
        return;
      }

      let matchedIndex; // find the row being edited in the tablebox data
      if (this.statusBeingEdited.Seq !== undefined && this.statusBeingEdited.Seq !== null) {
        // if Seq exists, strict comparison with undefined and null because Seq might be 0
        const foundIndex = this.statusCodeListTableData.findIndex(
          (statusCodeData) => statusCodeData.Seq === this.statusBeingEdited.Seq,
        );
        if (foundIndex !== -1) {
          // if a match was found with Seq
          matchedIndex = foundIndex; // assign the matching index
        }
      }

      if (matchedIndex === undefined && this.statusBeingEdited._rowid) {
        // if Seq does not exist or could not find a match with Seq, and _rowid exists
        const foundIndex = this.statusCodeListTableData.findIndex(
          (statusCodeData) => statusCodeData._rowid === this.statusBeingEdited._rowid,
        ); // try matching with _rowid instead
        if (foundIndex !== -1) {
          // if a match was found with _rowid
          matchedIndex = foundIndex; // assign the matching index
        }
      }
      if (matchedIndex === undefined) {
        // if a match still can't be found, which shouldn't happen
        throw new Error('Status - Could not find the row being deleted');
      }

      const originalStatusCode = this.statusBeingEdited.Status;

      if (this.selectedStatusCodeInput['Status'] !== originalStatusCode) {
        // Update the selected status properties to "0"
        this.statusCodeListTableData[matchedIndex].New = '0';
        this.statusCodeListTableData[matchedIndex].Edit = '0';
        this.statusCodeListTableData[matchedIndex].Delete = '0';

        // Add the new status code to the list
        this.statusCodeListTableData.push({
          Status: this.selectedStatusCodeInput.Status,
          'Status Description': this.selectedStatusCodeInput['Status Desc'],
          ...this.statusTypeValues,
          OriginalNew: '0',
          OriginalEdit: '0',
          OriginalDelete: '0',
          Seq: '',

          _action: 'N',
        });
      } else {
        // Update the data at the matched index if the status has not changed
        this.statusCodeListTableData.splice(matchedIndex, 1, {
          Status: this.selectedStatusCodeInput['Status'],
          'Status Description': this.selectedStatusCodeInput['Status Desc'],

          ...this.statusTypeValues,
          OriginalNew: this.statusCodeListTableData[matchedIndex]['OriginalNew'],
          OriginalEdit: this.statusCodeListTableData[matchedIndex]['OriginalEdit'],
          OriginalDelete: this.statusCodeListTableData[matchedIndex]['OriginalDelete'],
          Seq: this.statusBeingEdited?.Seq ?? '',

          _action: this.statusBeingEdited._action === 'N' ? 'N' : 'E', // if editing a new status code, leave as new, else set as edit
        });
      }

      const modifiedStatusCodeListTableData = this.statusCodeListTableData.map((statusCodeData) => {
        delete statusCodeData._rowid; // remove _rowid to force re-render
        return statusCodeData;
      });

      this.showStatusCodeTableBox = false; // hide TableBox and re-render on nextTick
      this.statusCodeListTableData = [];
      this.statusCodeSelectedRow = [];
      this.showStatusCodePopup = false;
      this.$nextTick(() => {
        this.statusCodeListTableData = modifiedStatusCodeListTableData;
        this.showStatusCodeTableBox = true; // re-render to force a re-execution of mounted in TableBox, in order to update _rowid
      });
    },

    cancelStatusAffirmativeClicked: function () {
      this.statusBeingEdited = null;
      this.showStatusCodePopup = false;
    },

    updateStatusTypeCheckBox: function (isNewChecked, isEditChecked, isDeleteChecked) {
      return [
        { checkBoxTitle: 'Add new', keyValue: '0', isChecked: isNewChecked },
        { checkBoxTitle: 'Edit / Update', keyValue: '1', isChecked: isEditChecked },
        { checkBoxTitle: 'Delete', keyValue: '2', isChecked: isDeleteChecked },
      ];
    },

    clearStatusPopupFields: function () {
      this.statusTypeCheckBox = this.updateStatusTypeCheckBox(false, false, false);
      this.statusTypeError = '';
      this.statusCodeError = '';
      this.statusCodeInputError = '';
      this.selectedStatusCode = [];
    },

    checkAddStatusPopupFields: function () {
      let error = false;
      if (this.selectedStatusCode.length == 0) {
        this.statusCodeError = 'Status Code is required!';
        error = true;
      }
      if (!this.statusTypeCheckBox.some((statusType) => statusType.isChecked)) {
        this.statusTypeError = 'Status Type is required!';
        error = true;
      }
      return error;
    },

    checkEditStatusPopupFields: function () {
      let error = false;
      if (!this.selectedStatusCodeInput) {
        this.statusCodeInputError = 'Status Code is required!';
        error = true;
      }
      if (!this.statusTypeCheckBox.some((statusType) => statusType.isChecked)) {
        this.statusTypeError = 'Status Type is required!';
        error = true;
      }
      return error;
    },

    checkFields: function () {
      this.clearPopupEntryMessages();
      let error = false;

      if (!this.roleIDInputValue) {
        this.roleIdError = 'Role ID is required!';
        this.popupEntryRequiredError.push({ Key: 'roleid', Value: 'Role ID is required!' });
        error = true;
      } else if (this.roleIDInputValue.length > 10) {
        this.roleIdError = 'Role ID cannot exceed 10 characters!';
        this.popupEntryRequiredError.push({ Key: 'roleid', Value: 'Role ID cannot exceed 10 characters!' });
        error = true;
      }
      if (this.roleNameInputValue.length > 50) {
        this.roleNameError = 'Role Name cannot exceed 50 characters!';
        this.popupEntryRequiredError.push({ Key: 'rolename', Value: 'Role Name cannot exceed 50 characters!' });
        error = true;
      }
      if (this.description.length > 200) {
        this.descriptionError = 'Description cannot exceed 200 characters!';
        this.popupEntryRequiredError.push({ Key: 'description', Value: 'Description cannot exceed 200 characters!' });
        error = true;
      }
      return error;
    },

    clearPopupEntryMessages: function () {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryMessages = [];
      this.popupEntryErrorMessages = [];
    },

    resetPopupEntryFields: function () {
      // Popup entry state
      this.popupEntryTitle = '';
      this.createdBy = '';
      this.updatedBy = '';
      this.saveAction = '';

      // Tab state
      this.currentTab = 0;

      // Popup entry common fields
      this.roleIDInputValue = '';
      this.roleIdError = '';
      this.roleNameInputValue = '';
      this.roleNameError = '';
      this.description = '';
      this.descriptionError = '';
      this.selectedParentRole = null;
      this.jobTypeRadioButtons = [
        { label: 'Import ', keyValue: 'IMPORT', checked: false },
        { label: 'Export ', keyValue: 'EXPORT', checked: false },
        { label: 'Both ', keyValue: 'BOTH', checked: true },
      ];
      this.adminTypeSU = null;
      this.adminTypeAA = null;
      this.adminTypeJA = null;

      // System Access
      this.accessListTableBoxSelectedRow = [];

      // Function Access
      this.selectedSystem = null;
      this.roleDetailsCache = {};
      // Users Bounded by Current Role
      this.usersBoundedTableData = [];
      this.usersBoundedTableBoxSelectedRow = [];
      this.userListMaster = this.getUserListMaster();

      // Status Code
      this.selectedStatusCode = [];
      this.statusCodeSelectedRow = [];
      this.statusCodeListTableData = [];
      this.statusTypeAdd = false;
      this.statusTypeEdit = false;
      this.statusTypeDelete = false;
      this.showStatusCodePopup = false;
    },

    popupEntryRequiredClicked: function (item) {
      const errorTabs = this.errorMapping.find((e) => e['key'] == item);

      if (this.currentTab != errorTabs['tab'] && errorTabs['tab'] >= 0) {
        this.currentTab = errorTabs['tab'];
      }
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    getRoleManagementList: async function () {
      this.roleMangementListTableBoxSelectedRow = [];
      this.roleManagementList = [];
      this.roleManagementList = await this.$apiCallV2('/RoleManagement/GetRoleMngmtListing');
    },

    getAccessList: async function () {
      this.accessListTableData = [];
      this.accessListTableData = await this.$apiCallV2(`/RoleManagement/GetDSMSystem?roleId=${this.roleIDInputValue}`);
    },


    getParentRoleListMaster: async function () {
      this.parentRoleListMaster = [];
      this.parentRoleListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ROLE' });
    },

    getstatusCodeListMaster: async function () {
      this.statusCodeListMaster = [];
      this.statusCodeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STATUS' });
    },

    getUserListMaster: async function () {
      this.userListMaster = [];
      this.userListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'USER' });
    },

    getSystemRoleDetails: async function (systemValue) {
      if (!systemValue) {
        return;
      }

      const systemKey = systemValue.System;

      this.roleDetails = null;

      if (this.roleDetailsCache[systemKey]) {
        this.roleDetails = this.roleDetailsCache[systemKey];
        return;
      }

      const systemRoleDetails = await this.$apiCallV2('/RoleManagement/GetRoleMngmtDetails', {
        roleid: this.roleIDInputValue,
        system: systemValue.System || '',
        getuserandstatus: '0',
      });
      if (
        systemRoleDetails &&
        systemRoleDetails.RoleDetails &&
        systemRoleDetails.RoleDetails.SystemAccess &&
        systemRoleDetails.RoleDetails.SystemAccess.ModuleAccess
      ) {
        systemRoleDetails.RoleDetails.SystemAccess.ModuleAccess.forEach((moduleAccess) => {
          moduleAccess.isChecked = moduleAccess.check === 'Yes';
          if (moduleAccess.FunctionAccess && moduleAccess.FunctionAccess.length > 0) {
            moduleAccess.FunctionAccess.forEach((functionAccess) => {
              functionAccess.isChecked = functionAccess.check === 'Yes';
            });
          }

          // Store original state
          moduleAccess.originalIsChecked = moduleAccess.isChecked;
          if (moduleAccess.FunctionAccess) {
            moduleAccess.FunctionAccess.forEach((fa) => {
              fa.originalIsChecked = fa.isChecked;
            });
          }
        });
        // Map function access details and set isDisabled property
        systemRoleDetails.RoleDetails.SystemAccess.ModuleAccess.forEach((ma) => {
          if (ma.FunctionAccess) {
            ma.FunctionAccess = ma.FunctionAccess.map((fa) => ({
              // Set isDisabled based on the isChecked status of the module access
              isDisabled: !ma.isChecked,
              ...fa,
            }));
          }
        });
        this.roleDetails = systemRoleDetails.RoleDetails;
        this.roleDetailsCache[systemKey] = systemRoleDetails.RoleDetails;
      }
    },

    getUserAndStatusRoleDetails: async function () {
      this.userAndStatusRoleDetails = [];
      this.userAndStatusRoleDetails = await this.$apiCallV2('/RoleManagement/GetRoleMngmtDetails', {
        roleid: this.roleIDInputValue,
        system: '',
        getuserandstatus: '1',
      });
      this.usersBoundedTableData =
        this.userAndStatusRoleDetails && this.userAndStatusRoleDetails.RoleDetails ?
          this.userAndStatusRoleDetails.RoleDetails.UsersBounded
        : [];
      const statusAccess =
        this.userAndStatusRoleDetails && this.userAndStatusRoleDetails.RoleDetails ?
          this.userAndStatusRoleDetails.RoleDetails.StatusAccess
        : [];
      if (statusAccess) {
        this.statusCodeListTableData = JSON.parse(JSON.stringify(statusAccess));
        this.statusCodeListTableData.forEach((f) => {
          f['OriginalNew'] = f['New'];
          f['OriginalEdit'] = f['Edit'];
          f['OriginalDelete'] = f['Delete'];
        });
      } else {
        this.statusCodeListTableData = [];
      }
    },

    populateRoleDetails: async function () {
      this.$fullscreenLoading('Getting Role Details');
      // Region tab-1
      this.roleIDInputValue = this.roleMangementListTableBoxSelectedRow[0]['Role ID'];
      this.roleNameInputValue = this.roleMangementListTableBoxSelectedRow[0]['Role Name'];
      this.description = this.roleMangementListTableBoxSelectedRow[0]['Description'];
      this.selectedParentRole = this.parentRoleListMaster.getSelected(
        this.roleMangementListTableBoxSelectedRow[0]['Parent Role'],
        'RoleID',
      );
      if (this.roleMangementListTableBoxSelectedRow[0]['Job Type']) {
        this.jobTypeRadioButtons.forEach((a) => {
          a['checked'] = false;
          if (a['keyValue'] == this.roleMangementListTableBoxSelectedRow[0]['Job Type']) {
            a['checked'] = true;
          }
        });
      }
      this.adminTypeAA = this.roleMangementListTableBoxSelectedRow[0]['ACAdmin'] === 'YES';
      this.adminTypeJA = this.roleMangementListTableBoxSelectedRow[0]['JobAdmin'] === 'YES';
      this.adminTypeSU = this.roleMangementListTableBoxSelectedRow[0]['Super'] === 'YES';
      this.createdBy = `${this.roleMangementListTableBoxSelectedRow[0]['User Created']} ${this.roleMangementListTableBoxSelectedRow[0]['Time Created'] ? new Date(this.roleMangementListTableBoxSelectedRow[0]['Time Created']).formatString('dd/MM/yyyy HH:mm') : ''}`;
      this.updatedBy = `${this.roleMangementListTableBoxSelectedRow[0]['User ID']} ${new Date(this.roleMangementListTableBoxSelectedRow[0]['Date Update']) ? new Date(this.roleMangementListTableBoxSelectedRow[0]['Date Update']).formatString('dd/MM/yyyy') : ''} ${this.roleMangementListTableBoxSelectedRow[0]['Time Update'].slice(0, 2)}:${this.roleMangementListTableBoxSelectedRow[0]['Time Update'].slice(2, 4)}`;

      // System access
      await this.getAccessList();

      // Function access
      const filteredSystems = this.accessListTableData.filter((system) => system.PropertiesValue === 'TRUE');
      if (filteredSystems.length > 0) {
        this.selectedSystem = filteredSystems[0];
      }
      this.getSystemRoleDetails(this.selectedSystem);

      // User bounded & Status code
      await this.getUserAndStatusRoleDetails();
      this.$fullscreenLoading();
    },

    tabClicked: async function (_tabItem, tabIndex) {
      if (this.showStatusCodePopup && tabIndex !== 3) {
        const discardChanges = await this.promptDiscardStatusCodeChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.showStatusCodePopup = false;
        this.statusBeingEdited = null;
      }

      // Function Acess Tab
      if (tabIndex === 1) {
        this.selectedSystem = null; // clear out the selection to prevent the Denied System Access appearing in the list
      }
      this.currentTab = tabIndex;
    },
    promptDiscardStatusCodeChanges: function () {
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved status code changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },

    moduleAccessCheckboxClicked: function (moduleAccess) {
      if (!moduleAccess.isChecked) {
        if (moduleAccess.FunctionAccess) {
          moduleAccess.FunctionAccess = moduleAccess.FunctionAccess.map((functionAccess) => {
            if (functionAccess.InternalSetting !== '1') {
              functionAccess.isChecked = false;
              functionAccess.isDisabled = true;
            }
            return functionAccess;
          });
        }
      } else if (moduleAccess.FunctionAccess) {
        moduleAccess.FunctionAccess = moduleAccess.FunctionAccess.map((functionAccess) => {
          if (functionAccess.InternalSetting !== '1') {
            functionAccess.isDisabled = false;
          }
          return functionAccess;
        });
      }
    },

    popupEntrySaveClicked: async function () {
      if (this.showStatusCodePopup) {
        const discardChanges = await this.promptDiscardStatusCodeChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showStatusCodePopup;
        this.statusBeingEdited = null;
      }
      this.clearPopupEntryMessages();

      const invalid = this.checkFields();
      const action = this.popupEntryTitle[0];
      const trimmedRoleIDInputValue = this.roleIDInputValue.trim();

      const functionaccessarr = [];
      if (this.roleDetailsCache) {
        const roleDetailsArr = Object.values(this.roleDetailsCache);

        if (roleDetailsArr && roleDetailsArr.length > 0) {
          roleDetailsArr.forEach(rda => {
            if (rda.SystemAccess && rda.SystemAccess.ModuleAccess) {
              rda.SystemAccess.ModuleAccess.forEach(moduleAccess => {
                // Create module access object
                if (moduleAccess.isChecked !== moduleAccess.originalIsChecked && moduleAccess.InternalSetting !== '1') { //check for InternalSettings, if is "1" cannot be changed
                  const moduleAccessObj = {
                    system: moduleAccess.System,
                    seqno: moduleAccess.SeqNo,
                    formid: moduleAccess.FormID,
                    control: moduleAccess.Control,
                    tabindex: moduleAccess.TabIndex,
                    statetype: moduleAccess.StateType,
                    checked: moduleAccess.isChecked ? 'TRUE' : 'FALSE',
                  };

                  // Add the module access object to the accumulator
                  functionaccessarr.push(moduleAccessObj);
                }

                // Check if FunctionAccess is an array before iterating
                moduleAccess.FunctionAccess?.forEach((functionAccess) => {
                  if (functionAccess.isChecked !== functionAccess.originalIsChecked && functionAccess.InternalSetting !== '1') { //check for InternalSettings, if is "1" cannot be changed
                    const functionAccessObj = {
                      system: functionAccess.System,
                      seqno: functionAccess.SeqNo,
                      formid: functionAccess.FormID,
                      control: functionAccess.Control,
                      tabindex: functionAccess.TabIndex,
                      statetype: functionAccess.StateType,
                      checked: functionAccess.isChecked ? 'TRUE' : 'FALSE',
                    };
                    functionaccessarr.push(functionAccessObj);
                  }
                });
              });
            }
          });
        }
      }
      
      if (!invalid) {
        const dataToPost = {
          action: this.saveAction,

          // Region tab-1
          roleid: trimmedRoleIDInputValue,
          rolename: this.roleNameInputValue,
          roledesc: this.description,
          compname: '6G',
          headroleid: this.selectedParentRole?.['RoleID'] ?? '',
          fmsjobtype: this.jobTypeRadioButtons.find((job) => job.checked)?.keyValue || '',
          superuser: this.adminTypeSU ? 'TRUE' : 'FALSE',
          accountadmin: this.adminTypeAA ? 'TRUE' : 'FALSE',
          jobadmin: this.adminTypeJA ? 'TRUE' : 'FALSE',

          systemaccess: this.accessListTableData
            .filter((system) => system.PropertiesValue === 'TRUE')
            .map((system) => {
              return {
                system: system.System,
              };
            }),

          // Function access
          functionaccess: functionaccessarr,
          
          // Users bounded
          userbounded: this.usersBoundedTableData
            .filter((f) => f['usersBounded_action'] && f['usersBounded_action'] != '')
            .map((user) => {
              return {
                userboundedid: user['User ID'],
                userboundedaction: user['usersBounded_action'],
              };
            }),

          // Status code
          statusaccess: this.statusCodeListTableData.reduce((acc, status) => {
            if (status['New'] !== status['OriginalNew']) {
              acc.push({
                roleid: this.roleIDInputValue,
                statusaction: status['New'] === '0' ? 'D' : 'N',
                status: status['Status'],
                useraction: 'N',
              });
            }

            if (status['Edit'] !== status['OriginalEdit']) {
              acc.push({
                roleid: this.roleIDInputValue,
                statusaction: status['Edit'] === '0' ? 'D' : 'N',
                status: status['Status'],
                useraction: 'E',
              });
            }

            if (status['Delete'] !== status['OriginalDelete']) {
              acc.push({
                roleid: this.roleIDInputValue,
                statusaction: status['Delete'] === '0' ? 'D' : 'N',
                status: status['Status'],
                useraction: 'D',
              });
            }

            return acc;
          }, []),
        };
        this.$fullscreenLoading('Saving Role Details');
        const result = await this.$apiCallV2('/RoleManagement/SaveRoleManagement', dataToPost);
        this.$fullscreenLoading();

        if (result[0]['Results'] != '0') {
          // Save error
          this.popupEntryErrorMessages.push(result[0]['ErrMsg']);
          return;
        }
        // Save successful
        this.viewMode = true;
        this.viewModePK = true;
        this.popupEntryTitle = 'View Role';
        this.showStatusCodeTableBox = false;
        this.$nextTick(() => {
          // hide and re-show TableBoxes to trigger a re-execution of TableBox's mounted, so that the additional buttons get hidden
          this.showStatusCodeTableBox = true;
        });
        this.getRoleManagementList();

        if (action == 'N') {
          this.popupEntrySuccessMessages.push(`Successfully Saved Role Details for: ${this.roleIDInputValue}`);
        } else {
          this.popupEntrySuccessMessages.push(`Successfully Saved Role Details for: ${this.roleIDInputValue}`);
        }
      }
    },
  },

  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Role Management') {
        x.data.forEach((k) => {
          //key in menu same as control value from api
          const selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });

    await this.getRoleManagementList();
    await this.getstatusCodeListMaster();
    await this.getUserListMaster();
    await this.getAccessList();
  },

  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.role-management-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;
  font-size: 12px;
  > .table-box-container {
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    background-color: white;
    border-radius: 5px;
    display: flex;
    overflow: hidden;
  }

  .popup-entry-component {
    .general-inputs {
      align-items: center;
      height: 150px;

      > .roleID-and-roleName {
        display: flex;
        align-items: center;

        > * {
          width: 100%;
        }

        > :not(:first-child) {
          margin-left: 5px;
        }
      }

      > .company-and-parentRole {
        display: flex;
        align-items: center;

        > * {
          width: 100%;
        }

        > :not(:first-child) {
          margin-left: 5px;
        }
      }

      > .jobType-and-admin {
        display: flex;

        > * {
          width: 100%;
        }

        > :not(:first-child) {
          margin-left: 5px;
        }
      }

      .jobType-radio-button {
        display: flex;
        flex-direction: column;

        > .radio-button-set-component:nth-child(2) {
          margin-top: 5px;
        }
      }

      .adminType-checkBox {
        display: flex;
      }
    }

    .tab-component {
      height: calc(100% - 150px);
      padding-top: 10px;

      .system-access {
        height: 100%;
        overflow: hidden;
        display: flex;

        > .table-box-container {
          width: 100%;
          margin-right: 80px;
          display: flex;
          overflow: hidden;
        }

        > .call-to-action-buttons {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          margin-bottom: 2px;
          height: 30px;

          > button:nth-child(1) {
            margin-right: 5px;
          }
        }
      }

      .checkbox-container {
        padding-bottom: 5px; /* Adjust margin as needed */
        padding-top: 5px;
      }

      .user-Bounded {
        display: flex;
        height: 100%;
        flex-direction: row;

        > .user-Bounded-List {
          height: 100%;
          margin-right: 3px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          flex: 1;

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin-bottom: 2px;
            height: 30px;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }

          > .table-box-container {
            width: 100%;
            display: flex;
            overflow: hidden;
          }

          > .user-List-Popup {
            height: calc(100% - #{$top-nav-height});
            width: 100%;
            background-color: rgba(0, 0, 0, 0.3);
            position: fixed;
            top: $top-nav-height;
            left: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 999;

            > .user-List {
              width: 85%;
              height: 85%;
              background-color: white;
              display: flex;
              flex-direction: column;
              border-radius: 5px;
              padding: 5px;

              > .table-box-container {
                overflow-y: hidden;
                height: 100%;
                display: flex;
                flex-direction: column;
              }

              > .call-to-action-buttons {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                margin-top: 5px;
                margin-bottom: 2px;
                height: 30px;

                > button:nth-child(1) {
                  margin-right: 5px;
                }
              }
            }
          }
        }
      }
      .status-Code {
        display: flex;
        height: 100%;
        flex-direction: row;

        > .status-Code-List {
          height: 100%;
          margin-right: 3px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          flex: 1;

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin-bottom: 2px;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }

          > .table-box-container {
            overflow: hidden;
          }
        }

        > .select-Status-Code-Popup {
          width: 50%;
          height: 100%;
          margin-left: 3px;
          flex-direction: column;
          display: flex;

          .status-filter {
            width: 100%;
            height: 150px;
            padding: 5px;
          }

          > .call-to-action-buttons {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin-bottom: 2px;

            > button:nth-child(1) {
              margin-right: 5px;
            }
          }
        }
      }
    }
  }
}

@media screen and (min-width: 1000px) {
  .function-access-container {
    display: inline-grid;
    grid-template-columns: auto auto auto auto;
  }
}
@media screen and (max-width: 1000px) {
  .function-access-container {
    display: inline-grid;
    grid-template-columns: auto auto auto;
  }
}
@media screen and (max-width: 600px) {
  .function-access-container {
    display: inline-grid;
    grid-template-columns: auto auto;
  }
}
</style>
